import DOMPurify from 'dompurify'
import { buildSpatialInsightsCard } from './spatial-insights-card.js'
import { buildHistogramSVG, buildScatterSVG } from './esda-charts.js'

/** Build the ESDA chart card HTML from an onResultUpdate esda payload. */
function buildEsdaCard(esda) {
  if (!esda) return ''
  if (esda.kind === 'histogram') return buildHistogramSVG(esda.hist, esda.label)
  if (esda.kind === 'scatter')
    return buildScatterSVG(esda.points, esda.labelX, esda.labelY, esda.correlation)
  return ''
}

/**
 * UI Overlay — HUD elements: header, compact bottom bar, toast notifications,
 * result summary panel, DuckDB status indicator
 *
 * Layout:
 *   Header bar (top) — logo, analytics indicator, reset, help
 *   Globe (full screen, unobstructed)
 *   Toast (temporary notification, appears above bottom bar)
 *   Bottom bar (compact ~60px) — mic button, text input, send, status line
 *   Result panel (top-left, slides in)
 *   Legend panel (bottom-right, managed by Legend class)
 */

class UIOverlay {
  constructor(container) {
    this.container = container
    this.elements = {}
    this._toastTimer = null

    // The text input and mic button are live from page load, but main.js wires
    // onTextSubmit / onVoiceToggle only after async init (DuckDB, router, …). An
    // action taken in that window used to be silently dropped (the "cold-start
    // no-op"). Queue it and replay once the handler is assigned, so the first
    // command always runs — whether typed or spoken.
    this._onTextSubmit = null
    this._pendingSubmit = null
    Object.defineProperty(this, 'onTextSubmit', {
      get() {
        return this._onTextSubmit
      },
      set(fn) {
        this._onTextSubmit = fn
        if (typeof fn === 'function' && this._pendingSubmit != null) {
          const queued = this._pendingSubmit
          this._pendingSubmit = null
          fn(queued)
        }
      },
    })

    // Same protection for the mic toggle: a click before wiring is remembered
    // and replayed once main.js assigns onVoiceToggle, so the mic actually
    // starts instead of doing nothing on the first tap.
    this._onVoiceToggle = null
    this._pendingVoiceToggle = false
    Object.defineProperty(this, 'onVoiceToggle', {
      get() {
        return this._onVoiceToggle
      },
      set(fn) {
        this._onVoiceToggle = fn
        if (typeof fn === 'function' && this._pendingVoiceToggle) {
          this._pendingVoiceToggle = false
          fn()
        }
      },
    })

    this._build()
    this._bindKeys()
  }

  _build() {
    this.container.innerHTML = ''

    // --- Header Bar ---
    const header = document.createElement('div')
    header.className = 'header-bar'
    header.innerHTML = `
      <div class="header-logo">Earth<span>Voice</span></div>
      <div class="header-controls">
        <div class="duckdb-indicator" id="duckdb-status" title="DuckDB Analytics Engine">
          <span class="duckdb-dot"></span>
          <span class="duckdb-label">Analytics</span>
        </div>
        <button class="header-btn" id="btn-layers" title="Stack mode — keep layers as you add them">&#9776;</button>
        <button class="header-btn" id="btn-lessons" title="Lesson Mode">&#128218;</button>
        <button class="header-btn moon-toggle-btn" id="btn-moon-toggle" title="MoonVoice — Explore the Lunar Surface">
          <svg viewBox="0 0 64 64" width="22" height="22">
            <defs>
              <radialGradient id="moon-g" cx="40%" cy="38%" r="50%">
                <stop offset="0%" stop-color="#d8d8d0"/>
                <stop offset="60%" stop-color="#a8a8a0"/>
                <stop offset="100%" stop-color="#686860"/>
              </radialGradient>
              <radialGradient id="moon-shadow" cx="80%" cy="50%" r="55%">
                <stop offset="0%" stop-color="transparent"/>
                <stop offset="70%" stop-color="rgba(0,0,0,0.15)"/>
                <stop offset="100%" stop-color="rgba(0,0,0,0.45)"/>
              </radialGradient>
            </defs>
            <circle cx="32" cy="32" r="30" fill="url(#moon-g)"/>
            <!-- Maria (dark patches) -->
            <ellipse cx="22" cy="20" rx="8" ry="6" fill="rgba(80,80,72,0.45)" transform="rotate(-15 22 20)"/>
            <ellipse cx="38" cy="16" rx="5" ry="4" fill="rgba(80,80,72,0.35)"/>
            <ellipse cx="26" cy="34" rx="10" ry="7" fill="rgba(75,75,68,0.4)" transform="rotate(10 26 34)"/>
            <ellipse cx="44" cy="30" rx="6" ry="8" fill="rgba(70,70,64,0.4)" transform="rotate(-20 44 30)"/>
            <ellipse cx="36" cy="44" rx="7" ry="5" fill="rgba(80,80,72,0.35)" transform="rotate(5 36 44)"/>
            <ellipse cx="18" cy="46" rx="4" ry="3" fill="rgba(85,85,78,0.3)"/>
            <!-- Bright crater ray -->
            <circle cx="40" cy="48" r="3.5" fill="rgba(200,200,195,0.4)"/>
            <circle cx="40" cy="48" r="1.8" fill="rgba(220,220,215,0.5)"/>
            <!-- Small craters -->
            <circle cx="14" cy="28" r="2" fill="rgba(90,90,82,0.3)"/>
            <circle cx="50" cy="20" r="2.5" fill="rgba(85,85,78,0.25)"/>
            <circle cx="30" cy="52" r="1.5" fill="rgba(90,90,82,0.25)"/>
            <!-- Limb shadow for 3D depth -->
            <circle cx="32" cy="32" r="30" fill="url(#moon-shadow)"/>
            <!-- Subtle edge highlight -->
            <circle cx="32" cy="32" r="30" fill="none" stroke="rgba(180,180,170,0.3)" stroke-width="0.8"/>
          </svg>
        </button>
        <button class="header-btn reset-btn" id="btn-reset" title="Reset view (Esc)">&#127758;</button>
        <button class="header-btn" id="btn-help" title="Help">?</button>
      </div>
    `
    this.container.appendChild(header)

    // --- Result Summary Panel (hidden by default) ---
    const resultPanel = document.createElement('div')
    resultPanel.className = 'result-panel'
    resultPanel.id = 'result-panel'
    resultPanel.innerHTML = `
      <div class="result-panel-header">
        <span class="result-panel-title">Results</span>
        <button class="result-panel-close" id="result-panel-close">&times;</button>
      </div>
      <div class="result-panel-body" id="result-panel-body"></div>
      <div class="result-panel-sql-toggle">
        <button class="result-panel-sql-btn" id="result-sql-toggle">Show SQL</button>
      </div>
      <pre class="result-panel-sql" id="result-panel-sql"></pre>
    `
    this.container.appendChild(resultPanel)
    this._restoreResultPanelWidth(resultPanel)
    this._observeResultPanelWidth(resultPanel)

    // --- Toast notification (temporary, above bottom bar) ---
    const toast = document.createElement('div')
    toast.className = 'toast-notification'
    this.container.appendChild(toast)

    // --- Choropleth Mode Switcher (hidden by default) ---
    const modeSwitcher = document.createElement('div')
    modeSwitcher.className = 'choropleth-mode-switcher'
    modeSwitcher.id = 'choropleth-mode-switcher'
    modeSwitcher.innerHTML = `
      <button class="mode-btn active" data-mode="flat" title="Flat choropleth">
        <svg viewBox="0 0 20 20" width="16" height="16"><rect x="3" y="8" width="14" height="4" rx="1" fill="currentColor"/></svg>
      </button>
      <button class="mode-btn" data-mode="extruded" title="3D extruded view">
        <svg viewBox="0 0 20 20" width="16" height="16"><rect x="3" y="4" width="4" height="12" rx="0.5" fill="currentColor"/><rect x="8" y="7" width="4" height="9" rx="0.5" fill="currentColor"/><rect x="13" y="2" width="4" height="14" rx="0.5" fill="currentColor"/></svg>
      </button>
    `
    this.container.appendChild(modeSwitcher)

    // --- Data Attribution Panel (bottom-left, collapsed by default) ---
    const attrBtn = document.createElement('button')
    attrBtn.className = 'attribution-toggle'
    attrBtn.id = 'attribution-toggle'
    attrBtn.title = 'Data Sources'
    attrBtn.innerHTML = 'ⓘ'
    this.container.appendChild(attrBtn)

    const attrPanel = document.createElement('div')
    attrPanel.className = 'attribution-panel'
    attrPanel.id = 'attribution-panel'
    attrPanel.innerHTML = `
      <div class="attribution-header">
        <span>Data Sources</span>
        <button class="attribution-close" id="attribution-close">&times;</button>
      </div>
      <ul class="attribution-list">
        <li><a href="https://www.geoboundaries.org/" target="_blank" rel="noopener">geoBoundaries</a> — <span class="attr-license">Open Database License (ODbL)</span></li>
        <li><a href="https://overturemaps.org/" target="_blank" rel="noopener">Overture Maps Foundation</a> — <span class="attr-license">CDLA Permissive 1.0</span></li>
        <li><a href="https://data.worldbank.org/" target="_blank" rel="noopener">World Bank Open Data</a> — <span class="attr-license">CC BY 4.0</span></li>
        <li><a href="https://www.maptiler.com/copyright/" target="_blank" rel="noopener">MapTiler</a> — <span class="attr-license">Satellite Imagery &amp; Terrain</span></li>
        <li><a href="https://www.naturalearthdata.com/" target="_blank" rel="noopener">Natural Earth</a> — <span class="attr-license">Public Domain</span></li>
      </ul>
    `
    this.container.appendChild(attrPanel)

    attrBtn.addEventListener('click', () => {
      attrPanel.classList.toggle('visible')
    })
    attrPanel.querySelector('#attribution-close').addEventListener('click', () => {
      attrPanel.classList.remove('visible')
    })

    // --- Compact Bottom Bar ---
    const bottomBar = document.createElement('div')
    bottomBar.className = 'bottom-bar'

    // Top row: mic + input + send
    const inputRow = document.createElement('div')
    inputRow.className = 'bottom-bar-input-row'

    const voiceBtn = document.createElement('button')
    voiceBtn.className = 'bottom-bar-mic'
    voiceBtn.innerHTML = '&#127908;'
    voiceBtn.title = 'Click or press Space to speak'
    inputRow.appendChild(voiceBtn)

    const textInput = document.createElement('input')
    textInput.type = 'text'
    textInput.className = 'bottom-bar-text'
    textInput.placeholder = 'Type a command... e.g. "Go to Japan"'
    inputRow.appendChild(textInput)

    const sendBtn = document.createElement('button')
    sendBtn.className = 'bottom-bar-send'
    sendBtn.innerHTML = '&#9654;'
    sendBtn.title = 'Send'
    inputRow.appendChild(sendBtn)

    bottomBar.appendChild(inputRow)

    // Status line
    const statusLine = document.createElement('div')
    statusLine.className = 'bottom-bar-status'
    statusLine.innerHTML =
      '<span class="status-text">READY</span> <span class="status-sep">&middot;</span> Press <kbd>Space</kbd> to speak <span class="status-sep">&middot;</span> <kbd>Esc</kbd> to reset'
    bottomBar.appendChild(statusLine)

    this.container.appendChild(bottomBar)

    // Store references
    this.elements = {
      header,
      voiceBtn,
      textInput,
      sendBtn,
      statusLine,
      toast,
      bottomBar,
      layersBtn: header.querySelector('#btn-layers'),
      lessonsBtn: header.querySelector('#btn-lessons'),
      moonToggleBtn: header.querySelector('#btn-moon-toggle'),
      resetBtn: header.querySelector('#btn-reset'),
      helpBtn: header.querySelector('#btn-help'),
      duckdbStatus: header.querySelector('#duckdb-status'),
      modeSwitcher,
      resultPanel,
      resultPanelBody: resultPanel.querySelector('#result-panel-body'),
      resultPanelSQL: resultPanel.querySelector('#result-panel-sql'),
      resultSQLToggle: resultPanel.querySelector('#result-sql-toggle'),
      resultPanelClose: resultPanel.querySelector('#result-panel-close'),
    }

    // --- Event listeners ---
    voiceBtn.addEventListener('click', () => {
      this._triggerVoiceToggle()
    })

    textInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        this._submitText()
      }
    })

    sendBtn.addEventListener('click', () => {
      this._submitText()
    })

    // Result panel controls
    this.elements.resultPanelClose.addEventListener('click', () => {
      this.hideResultPanel()
    })

    this.elements.resultSQLToggle.addEventListener('click', () => {
      this._toggleSQL()
    })
  }

  // Fire the mic toggle, or queue it if main.js hasn't wired the handler yet
  // (cold-start protection — the setter replays it once assigned).
  _triggerVoiceToggle() {
    // CL-152 (BUG-045): text-first fallback — when speech recognition isn't
    // available (Safari/Firefox/iPad), the mic and spacebar route the student
    // to the text input instead of dead-ending or popping a mic-consent
    // modal for a mic that can never work.
    if (this._voiceUnavailable) {
      this._setStatusLine(
        '<span class="status-text">TYPE INSTEAD</span> <span class="status-sep">&middot;</span> Voice isn&#39;t available in this browser &mdash; type your question below'
      )
      this.elements.textInput?.focus()
      return
    }
    if (typeof this.onVoiceToggle === 'function') {
      this.onVoiceToggle()
    } else {
      this._pendingVoiceToggle = true
    }
  }

  /**
   * CL-152 (BUG-045): switch the bottom bar to text-first mode for browsers
   * without Web Speech recognition. The mic stays visible (muted) so the UI
   * doesn't mysteriously differ between browsers, but every voice entry
   * point — button and spacebar — redirects to typing.
   */
  setVoiceUnavailable() {
    this._voiceUnavailable = true
    const { voiceBtn, textInput, statusLine } = this.elements
    if (voiceBtn) {
      voiceBtn.classList.add('voice-unavailable')
      voiceBtn.title = 'Voice input isn’t supported in this browser — type instead'
      voiceBtn.setAttribute('aria-disabled', 'true')
    }
    if (textInput) {
      textInput.placeholder = 'Ask the globe anything... e.g. "Go to Japan"'
    }
    if (statusLine) {
      this._setStatusLine(
        '<span class="status-text">READY</span> <span class="status-sep">&middot;</span> Type your question below <span class="status-sep">&middot;</span> <kbd>Esc</kbd> to reset'
      )
    }
  }

  _submitText() {
    const text = this.elements.textInput.value.trim()
    if (text) {
      this.elements.textInput.value = ''
      // Reset/clear/start-over bypass processCommand to avoid isProcessing lock
      if (/^(?:reset|clear|start\s+over)$/i.test(text)) {
        this._resetCallback?.()
      } else if (typeof this.onTextSubmit === 'function') {
        this.onTextSubmit(text)
      } else {
        // Handler not wired yet (app still initializing) — queue the command;
        // the onTextSubmit setter replays it once main.js assigns the handler.
        this._pendingSubmit = text
      }
    }
  }

  _bindKeys() {
    document.addEventListener('keydown', (e) => {
      // Spacebar to toggle voice (only if not typing in an input)
      if (e.code === 'Space' && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault()
        this._triggerVoiceToggle()
      }

      // Escape key triggers reset
      if (e.key === 'Escape') {
        e.preventDefault()
        this._resetCallback?.()
      }
    })
  }

  /**
   * Set the reset button callback (also used by Escape key)
   */
  onReset(callback) {
    this._resetCallback = callback
    this.elements.resetBtn?.addEventListener('click', callback)
  }

  /**
   * Set the help button callback
   */
  onHelp(callback) {
    this.elements.helpBtn?.addEventListener('click', callback)
  }

  onLessons(callback) {
    this.elements.lessonsBtn?.addEventListener('click', callback)
  }

  /** CL-173 (H3.2b): header Layers button — toggles stack mode. */
  onLayers(callback) {
    this.elements.layersBtn?.addEventListener('click', callback)
  }

  /** Reflect stack mode on the Layers button (amber when active). */
  setLayersActive(on) {
    this.elements.layersBtn?.classList.toggle('header-btn-active', !!on)
  }

  onMoonToggle(callback) {
    this.elements.moonToggleBtn?.addEventListener('click', callback)
  }

  /**
   * Wire choropleth mode switcher buttons.
   * @param {Function} callback - (mode: string) => void
   */
  onChoroplethModeChange(callback) {
    const switcher = this.elements.modeSwitcher
    if (!switcher) return
    switcher.querySelectorAll('.mode-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const mode = btn.dataset.mode
        callback?.(mode)
      })
    })
  }

  /**
   * Show/hide the choropleth mode switcher.
   */
  showModeSwitcher(visible) {
    const switcher = this.elements.modeSwitcher
    if (!switcher) return
    switcher.classList.toggle('visible', visible)
  }

  /**
   * Highlight the active choropleth mode button.
   */
  setChoroplethMode(mode) {
    const switcher = this.elements.modeSwitcher
    if (!switcher) return
    switcher.querySelectorAll('.mode-btn').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.mode === mode)
    })
  }

  /**
   * Update the Moon toggle button to reflect the current globe mode.
   * @param {'earth'|'moon'} mode
   */
  setGlobeMode(mode) {
    const btn = this.elements.moonToggleBtn
    if (!btn) return

    if (mode === 'moon') {
      btn.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>'
      btn.title = 'EarthVoice — Return to Earth'
    } else {
      btn.innerHTML = `<svg viewBox="0 0 64 64" width="22" height="22">
            <defs>
              <radialGradient id="moon-g" cx="40%" cy="38%" r="50%">
                <stop offset="0%" stop-color="#d8d8d0"/>
                <stop offset="60%" stop-color="#a8a8a0"/>
                <stop offset="100%" stop-color="#686860"/>
              </radialGradient>
              <radialGradient id="moon-shadow" cx="80%" cy="50%" r="55%">
                <stop offset="0%" stop-color="transparent"/>
                <stop offset="70%" stop-color="rgba(0,0,0,0.15)"/>
                <stop offset="100%" stop-color="rgba(0,0,0,0.45)"/>
              </radialGradient>
            </defs>
            <circle cx="32" cy="32" r="30" fill="url(#moon-g)"/>
            <ellipse cx="22" cy="20" rx="8" ry="6" fill="rgba(80,80,72,0.45)" transform="rotate(-15 22 20)"/>
            <ellipse cx="38" cy="16" rx="5" ry="4" fill="rgba(80,80,72,0.35)"/>
            <ellipse cx="26" cy="34" rx="10" ry="7" fill="rgba(75,75,68,0.4)" transform="rotate(10 26 34)"/>
            <ellipse cx="44" cy="30" rx="6" ry="8" fill="rgba(70,70,64,0.4)" transform="rotate(-20 44 30)"/>
            <ellipse cx="36" cy="44" rx="7" ry="5" fill="rgba(80,80,72,0.35)" transform="rotate(5 36 44)"/>
            <ellipse cx="18" cy="46" rx="4" ry="3" fill="rgba(85,85,78,0.3)"/>
            <circle cx="40" cy="48" r="3.5" fill="rgba(200,200,195,0.4)"/>
            <circle cx="40" cy="48" r="1.8" fill="rgba(220,220,215,0.5)"/>
            <circle cx="14" cy="28" r="2" fill="rgba(90,90,82,0.3)"/>
            <circle cx="50" cy="20" r="2.5" fill="rgba(85,85,78,0.25)"/>
            <circle cx="30" cy="52" r="1.5" fill="rgba(90,90,82,0.25)"/>
            <circle cx="32" cy="32" r="30" fill="url(#moon-shadow)"/>
            <circle cx="32" cy="32" r="30" fill="none" stroke="rgba(180,180,170,0.3)" stroke-width="0.8"/>
          </svg>`
      btn.title = 'MoonVoice — Explore the Lunar Surface'
    }
  }

  /**
   * Enable/disable the Moon toggle button (disabled during transitions).
   */
  setMoonToggleEnabled(enabled) {
    const btn = this.elements.moonToggleBtn
    if (!btn) return
    btn.style.pointerEvents = enabled ? '' : 'none'
    btn.style.opacity = enabled ? '' : '0.4'
  }

  // --- Toast Notifications ---

  /**
   * Show a temporary toast notification above the bottom bar.
   * Auto-hides after the given duration.
   */
  _showToast(message, duration = 5000) {
    const toast = this.elements.toast
    toast.textContent = message
    toast.classList.add('visible')

    if (this._toastTimer) clearTimeout(this._toastTimer)
    this._toastTimer = setTimeout(() => {
      toast.classList.remove('visible')
    }, duration)
  }

  _hideToast() {
    if (this._toastTimer) clearTimeout(this._toastTimer)
    this.elements.toast.classList.remove('visible')
  }

  // --- DuckDB Status ---

  setDuckDBStatus(status) {
    const el = this.elements.duckdbStatus
    if (!el) return

    el.className = `duckdb-indicator ${status}`
    const label = el.querySelector('.duckdb-label')

    switch (status) {
      case 'loading':
        label.textContent = 'Loading...'
        el.title = 'DuckDB Analytics Engine loading (~33MB)'
        break
      case 'ready':
        label.textContent = 'Analytics'
        el.title = 'DuckDB Analytics Engine ready'
        break
      case 'error':
        label.textContent = 'Offline'
        el.title = 'DuckDB failed to load — using local data only'
        break
      default:
        label.textContent = 'Analytics'
        el.title = 'DuckDB Analytics Engine'
    }
  }

  // --- Result Panel ---

  showResultPanel(result, sql, spatialAdvice) {
    const panel = this.elements.resultPanel
    const body = this.elements.resultPanelBody
    const sqlEl = this.elements.resultPanelSQL

    if (!result) return

    // Tear down any previous virtual-table state (scroll listener, etc.)
    this._teardownVirtualTable()

    // Build result body
    let bodyHTML = ''
    let tableRows = null
    if (result.type === 'table' && result.rows) {
      bodyHTML = this._buildTableHTML(result.rows)
      tableRows = result.rows
    } else if (result.count !== undefined) {
      bodyHTML = `<div class="result-count">${result.count} result${result.count !== 1 ? 's' : ''}</div>`
    } else if (result.type === 'summary' && result.message) {
      // CL-261: summaries (e.g. vector-tile outlines) used to open an empty panel.
      bodyHTML = `<div class="result-count">${this._escapeHTML(String(result.message))}</div>`
    }

    // Append the Spatial Insights card when the advisor fired with
    // confidence ≥ 0.8. Safe to concatenate: buildSpatialInsightsCard
    // returns '' for low-confidence or missing advice and escapes all
    // interpolated text internally.
    if (spatialAdvice) {
      try {
        bodyHTML += buildSpatialInsightsCard(spatialAdvice)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.debug('[spatial-insights] card build failed:', err)
      }
    }

    // ESDA chart card (gap analysis G7): distribution histogram or bivariate
    // scatter. SVG is built from numbers + escaped labels and survives the
    // sanitize() below (svg profile enabled).
    if (result.esda) {
      try {
        bodyHTML += buildEsdaCard(result.esda)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.debug('[esda] chart build failed:', err)
      }
    }

    // Nothing to show → keep the panel closed rather than open an empty box.
    if (!bodyHTML) {
      this.hideResultPanel()
      return
    }

    // SECURITY: sanitized 2026-04-18 — bodyHTML interpolates DuckDB row values.
    // svg profile added 2026-06-13 for the ESDA chart cards (G7).
    body.innerHTML = DOMPurify.sanitize(bodyHTML, { USE_PROFILES: { html: true, svg: true } })

    if (tableRows) {
      this._initVirtualTable(tableRows)
    }

    // Set SQL
    if (sql) {
      sqlEl.textContent = sql
      this.elements.resultSQLToggle.style.display = ''
    } else {
      sqlEl.textContent = ''
      this.elements.resultSQLToggle.style.display = 'none'
    }
    sqlEl.classList.remove('visible')
    this.elements.resultSQLToggle.textContent = 'Show SQL'

    panel.classList.add('visible')
    this._onResultPanelChange?.(panel)
  }

  hideResultPanel() {
    this._teardownVirtualTable()
    this.elements.resultPanel.classList.remove('visible')
    this._onResultPanelChange?.(null)
  }

  /**
   * CL-262: notified with the panel element when the result panel opens and
   * with null when it closes, so the globe can keep content out from under it.
   */
  onResultPanelChange(callback) {
    this._onResultPanelChange = callback
  }

  _toggleSQL() {
    const sqlEl = this.elements.resultPanelSQL
    const btn = this.elements.resultSQLToggle
    if (sqlEl.classList.contains('visible')) {
      sqlEl.classList.remove('visible')
      btn.textContent = 'Show SQL'
    } else {
      sqlEl.classList.add('visible')
      btn.textContent = 'Hide SQL'
    }
  }

  /**
   * Build scaffolding for a searchable, virtualized result table.
   * Rows are NOT serialized here — the virtualizer renders only visible rows.
   * Underscore-prefixed keys (e.g. _centroid) are filtered from display.
   */
  _buildTableHTML(rows) {
    if (!rows || rows.length === 0) {
      return '<div class="result-empty">No results</div>'
    }

    const keys = Object.keys(rows[0]).filter((k) => !k.startsWith('_'))
    const colHTML = keys.map((k) => `<div class="result-cell result-head-cell">${k}</div>`).join('')

    return `
      <div class="result-search-wrap">
        <input type="text" class="result-search" id="result-search" placeholder="Search by name…" autocomplete="off" spellcheck="false" />
      </div>
      <div class="result-row-count" id="result-row-count">${rows.length.toLocaleString()} result${rows.length !== 1 ? 's' : ''}</div>
      <div class="result-table-head">${colHTML}</div>
      <div class="result-table-scroll" id="result-table-scroll">
        <div class="result-table-spacer" id="result-table-spacer">
          <div class="result-table-rows" id="result-table-rows"></div>
        </div>
        <div class="result-empty" id="result-no-matches" style="display:none">No matches</div>
      </div>
    `
  }

  /**
   * Wire up search + virtualized scroll + row clicks after the scaffolding
   * is rendered. Only renders the rows currently visible in the scroll
   * viewport, so a 600K-row dataset stays responsive.
   */
  _initVirtualTable(rows) {
    const body = this.elements.resultPanelBody
    const scroll = body.querySelector('#result-table-scroll')
    const spacer = body.querySelector('#result-table-spacer')
    const rowsEl = body.querySelector('#result-table-rows')
    const search = body.querySelector('#result-search')
    const counter = body.querySelector('#result-row-count')
    const noMatches = body.querySelector('#result-no-matches')
    if (!scroll || !rowsEl || !spacer) return

    const keys = Object.keys(rows[0]).filter((k) => !k.startsWith('_'))
    const ROW_HEIGHT = 28
    const OVERSCAN = 6

    const state = {
      rows,
      filtered: rows,
      keys,
      rowHeight: ROW_HEIGHT,
      lastStart: -1,
      lastEnd: -1,
    }

    const render = () => {
      const total = state.filtered.length
      spacer.style.height = `${total * state.rowHeight}px`

      if (total === 0) {
        rowsEl.innerHTML = ''
        if (noMatches) noMatches.style.display = ''
        return
      }
      if (noMatches) noMatches.style.display = 'none'

      const viewportH = scroll.clientHeight || 1
      const scrollTop = scroll.scrollTop
      const start = Math.max(0, Math.floor(scrollTop / state.rowHeight) - OVERSCAN)
      const end = Math.min(total, Math.ceil((scrollTop + viewportH) / state.rowHeight) + OVERSCAN)
      if (start === state.lastStart && end === state.lastEnd) return
      state.lastStart = start
      state.lastEnd = end

      const parts = []
      for (let i = start; i < end; i++) {
        const row = state.filtered[i]
        const top = i * state.rowHeight
        const clickable = row && row._centroid ? ' result-row-clickable' : ''
        parts.push(`<div class="result-row${clickable}" data-idx="${i}" style="top:${top}px">`)
        for (const k of state.keys) {
          let val = row?.[k]
          if (typeof val === 'number') val = val.toLocaleString()
          parts.push(
            `<div class="result-cell">${val == null ? '' : this._escapeHTML(String(val))}</div>`
          )
        }
        parts.push('</div>')
      }
      // SECURITY: row values are HTML-escaped above; class names and styles
      // are derived from constants only.
      rowsEl.innerHTML = parts.join('')
    }

    const onScroll = () => render()
    let searchTimer = null
    const onSearchInput = () => {
      if (searchTimer) clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        const q = (search.value || '').trim().toLowerCase()
        if (!q) {
          state.filtered = state.rows
        } else {
          state.filtered = state.rows.filter((r) => {
            const name = String(r.name ?? '').toLowerCase()
            return name.includes(q)
          })
        }
        if (counter) {
          const n = state.filtered.length
          counter.textContent = `${n.toLocaleString()} result${n !== 1 ? 's' : ''}${q ? ` (filtered)` : ''}`
        }
        scroll.scrollTop = 0
        state.lastStart = -1
        render()
      }, 80)
    }

    const onRowClick = (e) => {
      const rowEl = e.target.closest('.result-row-clickable')
      if (!rowEl) return
      const idx = Number(rowEl.dataset.idx)
      const row = state.filtered[idx]
      if (row && row._centroid && this._onResultRowClick) {
        this._onResultRowClick(row)
      }
    }

    scroll.addEventListener('scroll', onScroll, { passive: true })
    search?.addEventListener('input', onSearchInput)
    rowsEl.addEventListener('click', onRowClick)

    this._virtualTable = {
      teardown: () => {
        scroll.removeEventListener('scroll', onScroll)
        search?.removeEventListener('input', onSearchInput)
        rowsEl.removeEventListener('click', onRowClick)
        if (searchTimer) clearTimeout(searchTimer)
      },
    }

    render()
  }

  _teardownVirtualTable() {
    if (this._virtualTable?.teardown) {
      this._virtualTable.teardown()
    }
    this._virtualTable = null
  }

  _restoreResultPanelWidth(panel) {
    try {
      const saved = parseInt(localStorage.getItem('ev:resultPanelWidth'), 10)
      if (!Number.isFinite(saved)) return
      const clamped = Math.max(280, Math.min(saved, Math.floor(window.innerWidth * 0.9)))
      panel.style.width = `${clamped}px`
    } catch (_) {
      /* localStorage unavailable — ignore */
    }
  }

  _observeResultPanelWidth(panel) {
    if (typeof ResizeObserver === 'undefined') return
    let timer = null
    let initialFire = true
    const observer = new ResizeObserver((entries) => {
      if (initialFire) {
        initialFire = false
        return
      }
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        const w = Math.round(entries[0].contentRect.width)
        if (!w) return
        try {
          localStorage.setItem('ev:resultPanelWidth', String(w))
        } catch (_) {
          /* ignore */
        }
      }, 150)
    })
    observer.observe(panel)
  }

  /**
   * Register a callback fired when a result row with a centroid is clicked.
   * Receives the full row object (including _centroid: [lng, lat]).
   */
  setResultRowClick(callback) {
    this._onResultRowClick = callback
  }

  _escapeHTML(s) {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  // --- State updates ---

  _setStatusLine(html) {
    // SECURITY: sanitized 2026-04-18 — callers interpolate user transcript / error message
    this.elements.statusLine.innerHTML = DOMPurify.sanitize(html)
  }

  setIdle() {
    this.elements.voiceBtn.classList.remove('listening')
    this.elements.bottomBar.classList.remove('thinking')
    // CL-152 (BUG-045): text-first browsers never see "Press Space to speak".
    this._setStatusLine(
      this._voiceUnavailable
        ? '<span class="status-text">READY</span> <span class="status-sep">&middot;</span> Type your question below <span class="status-sep">&middot;</span> <kbd>Esc</kbd> to reset'
        : '<span class="status-text">READY</span> <span class="status-sep">&middot;</span> Press <kbd>Space</kbd> to speak <span class="status-sep">&middot;</span> <kbd>Esc</kbd> to reset'
    )
    this._hideToast()
  }

  setListening() {
    this.elements.voiceBtn.classList.add('listening')
    this.elements.bottomBar.classList.remove('thinking')
    this._setStatusLine(
      '<span class="status-text listening">LISTENING</span> <span class="status-sep">&middot;</span> <span class="status-transcript interim">...</span>'
    )
  }

  setInterimTranscript(text) {
    this._setStatusLine(
      `<span class="status-text listening">LISTENING</span> <span class="status-sep">&middot;</span> <span class="status-transcript interim">${text}</span>`
    )
  }

  setProcessing(transcript) {
    this.elements.voiceBtn.classList.remove('listening')
    this.elements.bottomBar.classList.add('thinking')
    this._setStatusLine(
      `<span class="status-text processing">THINKING</span> <span class="status-sep">&middot;</span> <span class="status-transcript">${transcript || 'Processing...'}</span>`
    )
  }

  /**
   * Cold-start "warming up" state — shown while a heavy WASM engine (DuckDB or
   * geolibre) loads on the user's first analytic/geometry query. Keeps the
   * thinking spinner but labels it WARMING UP so the wait is explained rather
   * than looking like a blank/stuck result.
   */
  setWarming(message) {
    this.elements.voiceBtn.classList.remove('listening')
    this.elements.bottomBar.classList.add('thinking')
    this._setStatusLine(
      `<span class="status-text processing">WARMING UP</span> <span class="status-sep">&middot;</span> <span class="status-transcript">${message || 'Loading engine...'}</span>`
    )
  }

  /**
   * Show progressively streaming educational text in the toast area.
   */
  showStreamingText(text) {
    const toast = this.elements.toast
    // Truncate display to last ~300 chars to keep it readable
    const displayText = text.length > 300 ? '...' + text.slice(-300) : text
    toast.textContent = displayText
    toast.classList.add('visible')
    // Keep the "thinking" status while streaming
    this.elements.bottomBar.classList.add('thinking')
    this._setStatusLine(
      '<span class="status-text processing">ANSWERING</span> <span class="status-sep">&middot;</span> <span class="status-transcript">Streaming response...</span>'
    )
  }

  setResult(transcript, description) {
    this.elements.voiceBtn.classList.remove('listening')
    this.elements.bottomBar.classList.remove('thinking')
    this._setStatusLine(
      '<span class="status-text success">DONE</span> <span class="status-sep">&middot;</span> Press <kbd>Space</kbd> to speak <span class="status-sep">&middot;</span> <kbd>Esc</kbd> to reset'
    )
    // Show voice response as a temporary toast
    if (description && description !== `"${transcript}"`) {
      this._showToast(description, 8000)
    }
  }

  setError(message) {
    this.elements.voiceBtn.classList.remove('listening')
    this.elements.bottomBar.classList.remove('thinking')
    this._setStatusLine(
      `<span class="status-text error">ERROR</span> <span class="status-sep">&middot;</span> <span class="status-transcript">${message}</span>`
    )
  }
}

export { UIOverlay }
export default UIOverlay
